import { Box, Checkbox, FormGroup, InputAdornment, Radio, RadioGroup, Stack, Typography, useFormControl } from "@mui/material";
import { Flex, selectableOptionSx } from "@zfp-akademie-a-s/zfp-design";
import { FieldLabel, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { HtmlParser } from "Components/_Shared/HtmlParser";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { QuestionOption, optionControlSx, printAnswerPaddingSx } from "./QuestionOption";
import { useIsPrintMode, useQuestionnaireContext } from "./QuestionnaireContext";
import type { QuestionnaireQuestionItemDetail } from "./types";

const IMG_BASE_URL = "https://zfp.prod1.nsure.cz/assets/investment/img/questionnaire";

interface Props {
  type: string;
  fieldName: string;
  options?: QuestionnaireQuestionItemDetail[];
  placeholder?: string | null;
  label: string;
  isRequired: boolean;
  tooltip?: string;
  isNested?: boolean;
  isSubQuestion?: boolean;
}

type QuestionLabelProps = {
  id: string;
  label: string;
  isRequired: boolean;
  tooltip?: string;
  isSubQuestion?: boolean;
};

// The question as the package's fields label theirs, "Question * (i)"; on paper without the asterisk and the (i).
const QuestionLabel = ({ id, label, isRequired, tooltip, isSubQuestion }: QuestionLabelProps) => {
  const isPrint = useIsPrintMode();
  const { t } = useResource();

  return (
    <Box mb={Spacing.M} sx={{ breakAfter: "avoid" }}>
      <Typography
        component="span"
        variant="body1"
        fontSize={isSubQuestion ? 15 : 16}
        fontWeight={500}
        color="text.primary"
        lineHeight={1.4}
      >
        <FieldLabel
          id={id}
          label={label}
          required={isRequired && !isPrint}
          info={tooltip && !isPrint ? <HtmlParser content={tooltip} /> : undefined}
          infoLabel={t(Resources.Common.MoreInfo)}
        />
      </Typography>
    </Box>
  );
};

/** Every rail in the questionnaire marks the error state of whatever it encloses. */
export const railBorderColor = (hasError: boolean) => (hasError ? "error.main" : "divider");

const COMPACT_ANSWER_MAX_LENGTH = 11;

/** Answers as short as "Ano"/"Ne" read fine side by side, and a tall column of them only wastes vertical space. */
const shouldRenderInRow = (options: QuestionnaireQuestionItemDetail[]) =>
  options.length > 1 &&
  options.every((option) => option.name.length <= COMPACT_ANSWER_MAX_LENGTH && (option.childrenQuestionItems?.length ?? 0) === 0);

const AnswerOptionList = ({ withRail, isRow, children }: { withRail: boolean; isRow?: boolean; children: ReactNode }) => {
  const formControl = useFormControl();

  return (
    <Stack
      direction={isRow ? "row" : "column"}
      flexWrap={isRow ? "wrap" : "nowrap"}
      useFlexGap
      alignItems={isRow ? "center" : "stretch"}
      spacing={isRow ? Spacing.M : Spacing.XS}
      pl={withRail ? Spacing.L : 0}
      ml={withRail ? Spacing.L : 0}
      borderLeft={withRail ? 2 : 0}
      borderColor={railBorderColor(!!formControl?.error)}
    >
      {children}
    </Stack>
  );
};

type ChildMultiCheckboxProps = {
  groupFieldName: string;
  itemId: string;
  label: string;
  disabled: boolean;
};

const ChildMultiCheckbox = ({ groupFieldName, itemId, label, disabled }: ChildMultiCheckboxProps) => {
  const { register, getValues } = useFormContext();
  // Read once, without subscribing: this field is registered during render, so watching it would feed
  // register -> state update -> re-render -> register back into itself.
  const isChecked = (getValues(groupFieldName) || []).includes(itemId);

  return (
    <QuestionOption
      disabled={disabled}
      control={<Checkbox size="small" sx={optionControlSx} value={itemId} defaultChecked={isChecked} {...register(groupFieldName)} />}
      label={label}
    />
  );
};

export const QuestionnaireAnswer = ({
  type,
  fieldName,
  options = [],
  label,
  isRequired = false,
  tooltip,
  isNested = false,
  isSubQuestion = false,
}: Props) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { isReadOnly, mode } = useQuestionnaireContext();
  const isPrint = mode === "print";
  // In the PDF the questionnaire is read-only by definition, but rendering every control greyed out would
  // make the document unreadable - so only the app dims its inputs.
  const isDisabled = isReadOnly && !isPrint;

  switch (type) {
    case "Dropdown":
    case "Select":
    case "Default": {
      // Dropdown option names carry markup, the other types' are plain text - and parsing those could eat a stray "<".
      const hasMarkupLabels = type === "Dropdown";

      return (
        <Controller
          name={fieldName}
          control={control}
          defaultValue={""}
          render={({ field }) => (
            <Stack>
              <QuestionLabel id={fieldName} label={label} isRequired={isRequired} tooltip={tooltip} isSubQuestion={isSubQuestion} />
              <RadioGroup {...field}>
                <AnswerOptionList withRail={!isNested} isRow={shouldRenderInRow(options)}>
                  {options.map((option) => {
                    const optionValue = option.questionnaireQuestionItemID.toString();
                    const isSelected = String(field.value) === optionValue;

                    const childItems = option.childrenQuestionItems ?? [];
                    const isPlanSpecific = fieldName.includes("_");
                    const planExternalId = isPlanSpecific ? fieldName.split("_")[1] : null;
                    const parentQuestionId = isPlanSpecific ? fieldName.split("_")[0] : null;

                    const getChildFieldName = (childItemId: number) =>
                      isPlanSpecific ? `${parentQuestionId}_${childItemId}_${planExternalId}` : childItemId.toString();

                    const childGroupFieldName = isPlanSpecific
                      ? `${parentQuestionId}_childGroup_${planExternalId}`
                      : `childGroup_${optionValue}`;

                    // Only a Number child is validated under the ___Value key, the rest under the plain field name.
                    const hasChildError =
                      !!errors[childGroupFieldName] ||
                      childItems.some((childItem) => {
                        const childFieldName = getChildFieldName(childItem.questionnaireQuestionItemID);
                        return !!errors[childFieldName] || !!errors[`${childFieldName}___Value`];
                      });

                    return (
                      <Box key={optionValue}>
                        <QuestionOption
                          value={optionValue}
                          control={<Radio size="small" sx={optionControlSx} />}
                          label={hasMarkupLabels ? <HtmlParser content={option.name} /> : option.name}
                          disabled={option.isDisabled || isDisabled}
                        />

                        {childItems.length > 0 && isSelected && (
                          <Box
                            mt={Spacing.M}
                            ml={Spacing.XL}
                            pl={Spacing.L}
                            borderLeft={2}
                            borderColor={railBorderColor(hasChildError)}
                            data-question-error={hasChildError}
                          >
                            {childItems.map((childItem) => {
                              const childFieldName = getChildFieldName(childItem.questionnaireQuestionItemID);

                              return (
                                <Box key={childItem.questionnaireQuestionItemID} mb={Spacing.M}>
                                  {childItem.type === "Number" && (
                                    <Flex sx={{ width: "350px" }}>
                                      <FormTextField
                                        control={control}
                                        name={`${childFieldName}___Value`}
                                        label={childItem.name}
                                        required
                                        type="number"
                                        disabled={isDisabled}
                                        slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                                        fullWidth
                                      />
                                    </Flex>
                                  )}

                                  {childItem.type === "MultiCheckbox" && (
                                    <ChildMultiCheckbox
                                      groupFieldName={childGroupFieldName}
                                      itemId={childItem.questionnaireQuestionItemID.toString()}
                                      label={childItem.name}
                                      disabled={isDisabled}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </AnswerOptionList>
              </RadioGroup>
            </Stack>
          )}
        />
      );
    }
    case "SelectWithImage":
      return (
        <Stack>
          <QuestionLabel id={fieldName} label={label} isRequired={isRequired} tooltip={tooltip} isSubQuestion={isSubQuestion} />
          <Controller
            name={fieldName}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup {...field}>
                <Flex flexDirection="row" flexWrap="wrap" gap={Spacing.L}>
                  {options.map((option) => {
                    const optionValue = option.questionnaireQuestionItemID.toString();
                    const isSelected = String(field.value) === optionValue;
                    const isOptionDisabled = option.isDisabled || isDisabled;

                    return (
                      <Box
                        key={optionValue}
                        component="label"
                        sx={[
                          selectableOptionSx,
                          {
                            borderColor: "divider",
                            display: "flex",
                            flexDirection: "column",
                            gap: Spacing.M,
                            width: isPrint ? "150px" : "230px",
                            p: isPrint ? Spacing.M : Spacing.L,
                            borderRadius: Radius.M,
                            breakInside: "avoid",
                          },
                        ]}
                      >
                        {option.imageUrl && (
                          <Box
                            component="img"
                            src={`${IMG_BASE_URL}/${option.imageUrl}`}
                            alt={option.name}
                            sx={{
                              width: "100%",
                              height: "auto",
                              objectFit: "cover",
                              borderRadius: Radius.S,
                              border: "2px solid",
                              borderColor: isSelected ? "divider" : "common.white",
                            }}
                          />
                        )}
                        <Flex alignItems="center" justifyContent="center" gap={Spacing.S}>
                          <Radio size="small" sx={optionControlSx} value={optionValue} disabled={isOptionDisabled} />
                          <Typography variant="body2" color="text.primary" fontWeight={isSelected ? 500 : 400}>
                            {option.name}
                          </Typography>
                        </Flex>
                      </Box>
                    );
                  })}
                </Flex>
              </RadioGroup>
            )}
          />
        </Stack>
      );

    case "MultiCheckbox": {
      return (
        <Stack>
          <QuestionLabel id={fieldName} label={label} isRequired={isRequired} tooltip={tooltip} isSubQuestion={isSubQuestion} />
          <Controller
            name={fieldName}
            control={control}
            defaultValue={[]}
            render={({ field }) => {
              const currentValues: string[] = Array.isArray(field.value) ? field.value : [];

              return (
                <FormGroup>
                  <AnswerOptionList withRail={!isNested} isRow={shouldRenderInRow(options)}>
                    {options.map((option) => {
                      const optionId = option.questionnaireQuestionItemID.toString();
                      const isSelected = currentValues.includes(optionId);
                      return (
                        <QuestionOption
                          key={optionId}
                          disabled={option.isDisabled || isDisabled}
                          control={
                            <Checkbox
                              size="small"
                              sx={optionControlSx}
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  field.onChange([...currentValues, optionId]);
                                } else {
                                  field.onChange(currentValues.filter((v) => v !== optionId));
                                }
                              }}
                            />
                          }
                          label={option.name}
                        />
                      );
                    })}
                  </AnswerOptionList>
                </FormGroup>
              );
            }}
          />
        </Stack>
      );
    }

    case "Text": {
      const valueFieldName = `${fieldName}___Value`;
      const value = String(getValues(valueFieldName) ?? "").trim();

      // An input clips a long answer on paper. Left empty, it stays an input - a box to fill in by hand.
      if (isPrint && value) {
        return (
          <AnswerOptionList withRail={!isNested}>
            <Typography
              variant="body2"
              color="text.primary"
              lineHeight={1.4}
              whiteSpace="pre-wrap"
              sx={{ ...printAnswerPaddingSx, overflowWrap: "anywhere" }}
            >
              {value}
            </Typography>
          </AnswerOptionList>
        );
      }

      return <FormTextField control={control} name={valueFieldName} disabled={isDisabled} fullWidth />;
    }

    default:
      console.error("Unknown question type");
      return null;
  }
};
